import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Eye, ShieldCheck, Compass } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import EditorialRibbon from "../components/EditorialRibbon";
import { SHOP_URL, IMAGES, RIBBON_ITEMS } from "../data/content";

const PILLARS = [
  {
    icon: Eye,
    title: "Demystification over mystification",
    desc: "We reject dogma and esoteric obfuscation. We present the historical methodologies associated with Stanford Research Institute, Project Stargate, and Gateway consciousness research with clinical clarity.",
  },
  {
    icon: Compass,
    title: "Personal sovereignty",
    desc: "No gurus, no intermediaries, no gatekeepers. “Your Own Remote Path” means you are the sole instrument and investigator of your conscious awareness.",
  },
  {
    icon: ShieldCheck,
    title: "Practical discernment",
    desc: "We teach practitioners to verify data objectively, separate imagination from sensory signal, and maintain emotional equilibrium throughout the work.",
  },
];

export default function About() {
  usePageMeta(
    "About — Consciousness Ascending",
    "Consciousness Ascending publishes disciplined educational guidebooks for remote viewing and astral projection. Sovereign, structured, yours."
  );
  return (
    <>
      <PageHero
        eyebrow="About // Consciousness Ascending"
        lines={["A discipline,", "not a doctrine."]}
        description="Consciousness Ascending was founded on a singular conviction: human perception is non-local by nature, and non-local perception is an engineering discipline that can be learned by anyone."
        image={IMAGES.about}
        imageAlt="Observer before the stargate"
        imageAspect="aspect-[4/3]"
        meta="PUBLISHING COLLECTIVE // YOUR OWN REMOTE PATH"
      />

      <EditorialRibbon items={RIBBON_ITEMS} />

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:px-10 lg:py-32" data-testid="about-pillars">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="eyebrow">The Purpose</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display mt-6 text-3xl font-light leading-[1.12] tracking-tight sm:text-4xl lg:text-5xl">
                Built on three standing commitments.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="body-muted mt-8 max-w-md text-sm leading-relaxed sm:text-base">
                We publish guidebooks — nothing more presumptuous. The work is
                yours; the manuals simply hold the flashlight.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <div className="space-y-px border border-white/[0.07] bg-white/[0.07]">
              {PILLARS.map((p, i) => (
                <Reveal key={p.title} delay={0.1 * i}>
                  <div className="group flex gap-6 bg-[#0A0C12] p-8 transition-colors duration-500 hover:bg-[#0F121B] lg:p-10">
                    <p.icon className="h-5 w-5 shrink-0 text-[#38BDF8]" aria-hidden="true" />
                    <div>
                      <h3 className="font-display text-2xl text-[#F0F2F8] transition-colors duration-300 group-hover:text-[#A78BFA]">
                        {p.title}
                      </h3>
                      <p className="body-muted mt-3 text-sm leading-relaxed">
                        {p.desc}
                      </p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-white/[0.06] bg-[#0C0E14]/40">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:px-10 lg:py-32">
          <div className="grid gap-14 lg:grid-cols-12">
            <Reveal className="lg:col-span-7">
              <blockquote className="font-display text-3xl font-light leading-[1.2] tracking-tight text-[#F0F2F8] sm:text-4xl lg:text-5xl">
                “You are the sole instrument
                <br />
                of your own investigation.”
              </blockquote>
              <p className="coordinate-badge mt-8">
                The Your Own Remote Path principle
              </p>
            </Reveal>
            <div className="lg:col-span-4 lg:col-start-9">
              <Reveal delay={0.15}>
                <p className="body-muted text-sm leading-relaxed sm:text-base">
                  Our publications and guidebooks are produced and distributed in
                  partnership with Fourthwall — for high-fidelity physical
                  books, immediate digital fulfillment, and secure handling of
                  every order.
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <Link
                    to="/guidebooks"
                    data-testid="about-guidebooks-btn"
                    className="group inline-flex items-center gap-2 border border-white/20 px-6 py-3.5 font-mono-ui text-[11px] uppercase tracking-[0.2em] text-[#F0F2F8] transition-all duration-300 hover:border-[#8B5CF6]"
                  >
                    Browse the library
                    <ArrowRight className="h-3.5 w-3.5 text-[#A78BFA] transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                  <a
                    href={SHOP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="about-shop-btn"
                    className="inline-flex items-center gap-2 border border-[#8B5CF6] bg-[#8B5CF6]/15 px-6 py-3.5 font-mono-ui text-[11px] uppercase tracking-[0.2em] text-[#F0F2F8] transition-all duration-300 hover:bg-[#8B5CF6]/30"
                  >
                    Fourthwall Shop <ArrowUpRight className="h-3.5 w-3.5 text-[#A78BFA]" />
                  </a>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
